// src/pages/Dashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [bids, setBids] = useState([]);
  const [userEmail, setUserEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // =========================================================
  // 1. VERIFY GOOGLE LOGIN
  // =========================================================
  useEffect(() => {
    const verifyAccess = async () => {
      try {
        const res = await fetch(
          "https://api.freebidnow.com/api/auth/me",
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!res.ok) {
          console.error("Authentication failed:", res.status);
          window.location.href = "/";
          return;
        }

        const user = await res.json();

        console.log("✅ Logged-in user:", user);

        setUserEmail(user.email || "");

      } catch (err) {
        console.error("❌ Error verifying access:", err);
        window.location.href = "/";
      } finally {
        setIsLoading(false);
      }
    };

    verifyAccess();
  }, []);

  // =========================================================
  // 2. LOGOUT
  // =========================================================
  const handleLogout = async () => {
    try {
      await fetch(
        "https://api.freebidnow.com/api/auth/logout",
        {
          method: "GET",
          credentials: "include",
        }
      );
    } catch (err) {
      console.error("❌ Logout request failed:", err);
    } finally {
      window.location.href = "https://freebidnow.com/";
    }
  };

  // =========================================================
  // 3. INITIALIZE GOOGLE MAP + LOAD NEARBY BIDS
  // =========================================================
  useEffect(() => {
    if (isLoading) return;

    const initMap = async () => {
      try {
        // -----------------------------------------------------
        // Check Google Maps
        // -----------------------------------------------------
        if (!window.google?.maps) {
          console.warn("⚠️ Google Maps has not loaded yet.");
          return;
        }

        // -----------------------------------------------------
        // Check browser geolocation
        // -----------------------------------------------------
        if (!navigator.geolocation) {
          alert("Geolocation is not supported by this browser.");
          return;
        }

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const userLocation = {
                lng: position.coords.longitude,
                lat: position.coords.latitude,
              };

              console.log("📍 User location:", userLocation);

              // -------------------------------------------------
              // Create Google Map
              // -------------------------------------------------
              const mapElement = document.getElementById("map");

              if (!mapElement) {
                console.error("❌ Map element not found.");
                return;
              }

              const map = new window.google.maps.Map(mapElement, {
                center: userLocation,
                zoom: 6,
              });

              // -------------------------------------------------
              // User marker
              // -------------------------------------------------
              const userMarker = new window.google.maps.Marker({
                position: userLocation,
                map,
                title: "You are here",
                icon: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              });

              const infoWindow = new window.google.maps.InfoWindow({
                content: `
                  <div>
                    <strong>You are here</strong><br/>
                    ${userLocation.lat.toFixed(4)},
                    ${userLocation.lng.toFixed(4)}
                  </div>
                `,
              });

              userMarker.addListener("click", () => {
                infoWindow.open({
                  map,
                  anchor: userMarker,
                });
              });

              // -------------------------------------------------
              // FETCH NEARBY BIDS
              // IMPORTANT:
              // Backend is api.freebidnow.com
              // -------------------------------------------------
              const bidUrl =
                `https://api.freebidnow.com/api/bids/nearby` +
                `?lng=${userLocation.lng}` +
                `&lat=${userLocation.lat}`;

              console.log("🔎 Fetching bids:", bidUrl);

              const res = await fetch(bidUrl, {
                method: "GET",
                credentials: "include",
                headers: {
                  Accept: "application/json",
                },
              });

              if (!res.ok) {
                const errorText = await res.text();

                console.error(
                  "❌ Bid API failed:",
                  res.status,
                  errorText
                );

                return;
              }

              const data = await res.json();

              console.log("✅ Loaded bids:", data);

              const loadedBids = Array.isArray(data) ? data : [];

              setBids(loadedBids);

              // -------------------------------------------------
              // Display each bid
              // -------------------------------------------------
              loadedBids.forEach((bid) => {

                // =================================================
                // PICKUP MARKER
                // =================================================
                const pickup = bid.location?.coordinates;

                if (
                  Array.isArray(pickup) &&
                  pickup.length === 2
                ) {
                  const [lng, lat] = pickup;

                  if (
                    typeof lat === "number" &&
                    typeof lng === "number" &&
                    !isNaN(lat) &&
                    !isNaN(lng)
                  ) {

                    const marker =
                      new window.google.maps.Marker({
                        position: {
                          lat,
                          lng,
                        },
                        map,
                        title: bid.details || "Bid",
                        icon:
                          "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                      });

                    // ---------------------------------------------
                    // Bid information
                    // ---------------------------------------------
                    const brokerEmail =
                      bid.brokerEmail?.trim() || "N/A";

                    const subject =
                      `Bid Request - ${
                        bid.details || "Load Request"
                      }`;

                    const destination =
                      bid.destination || "N/A";

                    const body = `
Hello Broker,

I am interested in this load:

${bid.details || "No details provided"}

Pickup Coordinates:
${lat.toFixed(4)}, ${lng.toFixed(4)}

Destination:
${destination}

Thank you.
                    `.trim();

                    const mailtoLink =
                      `mailto:${brokerEmail}` +
                      `?subject=${encodeURIComponent(subject)}` +
                      `&body=${encodeURIComponent(body)}`;

                    // ---------------------------------------------
                    // InfoWindow
                    // ---------------------------------------------
                    const bidInfo =
                      new window.google.maps.InfoWindow({
                        content: `
                          <div style="min-width:250px;">
                            <h3>
                              📦 ${
                                bid.details ||
                                "Bid Details"
                              }
                            </h3>

                            <p>
                              <strong>Pickup:</strong><br/>
                              ${lat.toFixed(4)},
                              ${lng.toFixed(4)}
                            </p>

                            <p>
                              <strong>Broker:</strong><br/>
                              ${
                                bid.broker ||
                                "N/A"
                              }
                            </p>

                            <p>
                              <strong>Email:</strong><br/>
                              ${brokerEmail}
                            </p>

                            <p>
                              <strong>Weight:</strong><br/>
                              ${
                                bid.weight != null
                                  ? `${bid.weight} lbs`
                                  : "N/A"
                              }
                            </p>

                            <p>
                              <strong>Miles:</strong><br/>
                              ${
                                bid.miles != null
                                  ? `${bid.miles} miles`
                                  : "N/A"
                              }
                            </p>

                            <p>
                              <strong>Destination:</strong><br/>
                              ${destination}
                            </p>

                            ${
                              brokerEmail !== "N/A"
                                ? `
                                  <a
                                    href="${mailtoLink}"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style="
                                      display:inline-block;
                                      margin-top:8px;
                                      padding:6px 10px;
                                      background:#22c55e;
                                      color:#fff;
                                      text-decoration:none;
                                      border-radius:5px;
                                    "
                                  >
                                    📧 Request via Gmail
                                  </a>
                                `
                                : ""
                            }

                          </div>
                        `,
                      });

                    marker.addListener("click", () => {
                      bidInfo.open({
                        map,
                        anchor: marker,
                      });
                    });
                  }
                }

                // =================================================
                // DESTINATION MARKER
                // =================================================
                const destination =
                  bid.destinationLocation?.coordinates;

                if (
                  Array.isArray(destination) &&
                  destination.length === 2
                ) {
                  const [destLng, destLat] = destination;

                  if (
                    typeof destLat === "number" &&
                    typeof destLng === "number" &&
                    !isNaN(destLat) &&
                    !isNaN(destLng)
                  ) {

                    const destMarker =
                      new window.google.maps.Marker({
                        position: {
                          lat: destLat,
                          lng: destLng,
                        },
                        map,
                        title: "Destination",
                        icon:
                          "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                      });

                    const destInfo =
                      new window.google.maps.InfoWindow({
                        content: `
                          <div>
                            <h3>📍 Destination</h3>

                            <p>
                              <strong>Coordinates:</strong><br/>
                              ${destLat.toFixed(4)},
                              ${destLng.toFixed(4)}
                            </p>
                          </div>
                        `,
                      });

                    destMarker.addListener("click", () => {
                      destInfo.open({
                        map,
                        anchor: destMarker,
                      });
                    });
                  }
                }
              });

            } catch (err) {
              console.error(
                "❌ Error loading map/bids:",
                err
              );
            }
          },

          // =====================================================
          // GEOLOCATION ERROR
          // =====================================================
          (error) => {
            console.error(
              "❌ Geolocation error:",
              error
            );

            if (error.code === 1) {
              alert(
                "Location access was denied. Please allow location access to display nearby bids."
              );
            } else if (error.code === 2) {
              alert(
                "Your location could not be determined."
              );
            } else if (error.code === 3) {
              alert(
                "Location request timed out. Please try again."
              );
            }
          },

          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 300000,
          }
        );

      } catch (err) {
        console.error(
          "❌ Map initialization error:",
          err
        );

        const mapDiv =
          document.getElementById("map");

        if (mapDiv) {
          mapDiv.innerHTML = `
            <div
              style="
                padding:20px;
                text-align:center;
                color:red;
              "
            >
              Map failed to load. Please refresh.
            </div>
          `;
        }
      }
    };

    // ===========================================================
    // Wait for Google Maps if it hasn't loaded yet
    // ===========================================================
    if (window.google?.maps) {
      initMap();
    } else {
      const interval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(interval);
          initMap();
        }
      }, 300);

      return () => clearInterval(interval);
    }

  }, [isLoading]);

  // ============================================================
  // LOADING SCREEN
  // ============================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 text-gray-700">
        <div className="text-center">

          <div className="loader mb-3"></div>

          <p>
            Loading your dashboard, please wait...
          </p>

          <style>{`
            .loader {
              border: 5px solid #f3f3f3;
              border-top: 5px solid #3498db;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto;
            }

            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }

              100% {
                transform: rotate(360deg);
              }
            }
          `}</style>

        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN DASHBOARD
  // ============================================================
  return (
    <div>

      {/* ======================================================
          HEADER
      ======================================================= */}
      <div className="flex justify-between items-center px-4 py-3 bg-gray-800 text-white">

        <div>
          <h2 className="text-xl font-semibold">
            Nearby Job Bids
          </h2>

          {userEmail && (
            <p className="text-sm text-gray-300">
              {userEmail}
            </p>
          )}
        </div>

        <div className="flex gap-3">

          <button
            onClick={handleLogout}
            className="bg-red-500 px-4 py-2 rounded hover:bg-red-600"
          >
            Logout
          </button>

        </div>

      </div>

      {/* ======================================================
          ACCOUNT INFORMATION
      ======================================================= */}
      <div className="text-center mt-3 text-gray-700">

        <p>
          Account:{" "}
          <strong className="text-green-600">
            Active
          </strong>
        </p>

        <p>
          Showing{" "}
          <strong>
            {bids.length}
          </strong>{" "}
          nearby bids
        </p>

      </div>

      {/* ======================================================
          MAP
      ======================================================= */}
      <div
        id="map"
        style={{
          height: "500px",
          width: "100%",
        }}
      ></div>

      {/* ======================================================
          BID COUNT
      ======================================================= */}
      <p className="text-center mt-4 text-gray-600">
        Showing {bids.length} nearby bids
      </p>

    </div>
  );
};

export default Dashboard;
